/**
 * Toast Container & Toast Component — Apex Design System.
 *
 * Fixed bottom-right stack per §8.7. Each toast has:
 * - Type icon (success/error/warning/info)
 * - Title + optional message
 * - Optional action button
 * - Close button
 * - Progress bar that depletes over the dismiss duration
 * - Enter: translateX(calc(100% + 32px)) → translateX(0), spring, 350ms
 * - Exit: translateX(calc(100% + 32px)) + opacity 0, ease-in, 250ms
 */

import { useState, useEffect } from "react";
import { CheckCircle, AlertCircle, AlertTriangle, Info, X } from "lucide-react";
import { useToastStore, type Toast, type ToastType } from "@/stores/toastStore";

const TYPE_CONFIG: Record<
  ToastType,
  {
    icon: React.ElementType;
    iconClass: string;
    barClass: string;
    borderClass: string;
  }
> = {
  success: {
    icon: CheckCircle,
    iconClass: "text-[var(--text-success)]",
    barClass: "bg-[var(--text-success)]",
    borderClass: "border-l-[var(--text-success)]",
  },
  error: {
    icon: AlertCircle,
    iconClass: "text-[var(--text-danger)]",
    barClass: "bg-[var(--text-danger)]",
    borderClass: "border-l-[var(--text-danger)]",
  },
  warning: {
    icon: AlertTriangle,
    iconClass: "text-[var(--text-warning)]",
    barClass: "bg-[var(--text-warning)]",
    borderClass: "border-l-[var(--text-warning)]",
  },
  info: {
    icon: Info,
    iconClass: "text-[var(--text-info)]",
    barClass: "bg-[var(--text-info)]",
    borderClass: "border-l-[var(--text-info)]",
  },
};

function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts);

  return (
    <div
      className="fixed bottom-8 right-8 z-[70] flex flex-col-reverse gap-2 pointer-events-none"
      aria-live="polite"
      aria-relevant="additions"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} />
      ))}
    </div>
  );
}

function ToastItem({ toast: t }: { toast: Toast }) {
  const removeToast = useToastStore((s) => s.removeToast);
  const [exiting, setExiting] = useState(false);
  const [progress, setProgress] = useState(100);

  const config = TYPE_CONFIG[t.type];
  const Icon = config.icon;

  // Progress bar animation.
  useEffect(() => {
    if (t.duration <= 0) return;
    const start = Date.now();
    const tick = () => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, 100 - (elapsed / t.duration) * 100);
      setProgress(remaining);
      if (remaining > 0) requestAnimationFrame(tick);
    };
    const raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [t.duration]);

  const handleClose = () => {
    setExiting(true);
    setTimeout(() => removeToast(t.id), 250);
  };

  return (
    <div
      role={t.type === "error" ? "alert" : "status"}
      className={`pointer-events-auto w-80 overflow-hidden rounded-[var(--radius-lg)] elevation-2 border-l-[3px] ${config.borderClass} ${
        exiting ? "animate-toast-exit" : "animate-toast-enter"
      }`}
      style={{
        animation: exiting
          ? "toastExit 250ms cubic-bezier(0.36, 0, 0.66, 0) forwards"
          : "toastEnter 350ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards",
      }}
    >
      {/* Content */}
      <div className="flex items-start gap-3 px-4 py-3">
        <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${config.iconClass}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[var(--text-primary)]">
            {t.title}
          </p>
          {t.message && (
            <p className="mt-0.5 text-xs text-[var(--text-secondary)] leading-relaxed">
              {t.message}
            </p>
          )}
          {t.action && (
            <button
              onClick={() => {
                t.action!.onClick();
                handleClose();
              }}
              className="mt-1.5 text-xs font-medium text-[var(--color-interactive-primary)] hover:underline"
            >
              {t.action.label}
            </button>
          )}
        </div>
        <button
          onClick={handleClose}
          className="shrink-0 mt-0.5 text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Progress bar (depletes over duration) */}
      {t.duration > 0 && (
        <div className="h-0.5 w-full bg-[var(--border-subtle)]">
          <div
            className={`h-full ${config.barClass} transition-none`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
}

export default ToastContainer;
